import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  User, Upload, Loader2, Lock, MapPin, Swords, LogOut, Gem, QrCode, Copy,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { api, compact } from '../lib/api';
import { useAuth } from '../lib/store';
import { Avatar, SectionHead, Sheet } from '../components/ui';

export default function Settings() {
  const nav = useNavigate();
  const { user, hero, city, logout, load } = useAuth();
  const fileRef = useRef();
  const [up, setUp] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [f, setF] = useState({ display_name: '', bio: '', city_group_id: '' });
  const [pw, setPw] = useState({ current: '', next: '' });

  const { data: cities } = useQuery({ queryKey: ['groups'], queryFn: () => api.get('/groups').then(r => r.data.filter(g => g.kind === 'city')) });
  const { data: meta } = useQuery({ queryKey: ['heroMeta'], queryFn: () => api.get('/hero/meta').then(r => r.data) });

  useEffect(() => {
    if (user) setF({ display_name: user.display_name || '', bio: user.bio || '', city_group_id: user.city_group_id || '' });
  }, [user?.id]);

  const saveM = useMutation({
    mutationFn: () => api.patch('/users/me', { ...f, city_group_id: f.city_group_id || null }),
    onSuccess: async () => { toast.success('Profile saved'); await load(); },
    onError: e => toast.error(e.message),
  });

  const pwM = useMutation({
    mutationFn: () => api.post('/auth/change-password', pw),
    onSuccess: () => { toast.success('Password changed'); setPw({ current: '', next: '' }); },
    onError: e => toast.error(e.message),
  });

  const classM = useMutation({
    mutationFn: (k) => api.post('/hero/class', { class_key: k }),
    onSuccess: async () => { toast.success('Class changed! New moves unlocked ⚔️'); await load(); },
    onError: e => toast.error(e.message),
  });

  async function upload(file) {
    if (!file) return;
    setUp(true);
    const fd = new FormData(); fd.append('file', file);
    try { await api.post('/upload/avatars', fd); await load(); toast.success('Photo updated'); }
    catch (e) { toast.error(e.message); } finally { setUp(false); }
  }

  return (
    <div className="space-y-4 animate-fade-up max-w-lg mx-auto">
      {/* PROFILE */}
      <div className="card p-5">
        <SectionHead title="Profile" sub="How others see you" />
        <div className="flex items-center gap-4">
          <button onClick={() => fileRef.current.click()} className="relative group">
            <Avatar src={user?.avatar_url} name={user?.username} size={72} />
            <div className="absolute inset-0 rounded-full bg-ink-900/50 grid place-items-center opacity-0 group-hover:opacity-100 transition">
              {up ? <Loader2 size={18} className="animate-spin text-white" /> : <Upload size={18} className="text-white" />}
            </div>
          </button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={e => upload(e.target.files[0])} />
          <div className="flex-1">
            <p className="font-bold">@{user?.username}</p>
            <p className="text-xs text-ink-400 font-semibold">{user?.email}</p>
            <p className="text-[11px] text-brand-600 font-black mt-1">💎 {compact(user?.points_balance)} points</p>
          </div>
        </div>

        <div className="space-y-3 mt-4">
          <div><label className="label">Display name</label>
            <input className="input" value={f.display_name} onChange={e => setF({ ...f, display_name: e.target.value })} /></div>
          <div><label className="label">Bio</label>
            <textarea className="input min-h-[70px]" maxLength={500} value={f.bio}
              onChange={e => setF({ ...f, bio: e.target.value })} placeholder="Tell the community about you…" /></div>
          <div><label className="label">City group</label>
            <select className="input" value={f.city_group_id} onChange={e => setF({ ...f, city_group_id: e.target.value })}>
              <option value="">No city</option>
              {cities?.map(c => <option key={c.id} value={c.id}>{c.handle} — {c.name}</option>)}
            </select>
            <p className="text-[11px] text-ink-400 font-semibold mt-1">
              <MapPin size={9} className="inline" /> Your city determines your local ranking & offers.
            </p></div>
          <button onClick={() => saveM.mutate()} disabled={saveM.isPending} className="btn-primary w-full">
            {saveM.isPending ? 'Saving…' : 'Save profile'}</button>
        </div>
      </div>

      {/* MY QR */}
      <div className="card p-5">
        <SectionHead title="My duel QR" sub="Let friends scan to challenge you" />
        <button onClick={() => setQrOpen(true)} className="btn-dark w-full"><QrCode size={15} /> Show my code</button>
      </div>

      {/* HERO CLASS */}
      {hero && meta && (
        <div className="card p-5">
          <SectionHead title="Hero class" sub="Changing class costs 2000 points" />
          <div className="grid grid-cols-2 gap-2">
            {Object.values(meta.classes).map(c => (
              <button key={c.key}
                onClick={() => hero.class_key !== c.key && confirm(`Change to ${c.name} for 2000 points?`) && classM.mutate(c.key)}
                className={`p-3 rounded-xl border-2 text-left transition
                  ${hero.class_key === c.key ? 'border-brand-500 bg-brand-50' : 'border-ink-200 hover:border-ink-300'}`}>
                <span className="text-xl">{c.icon}</span>
                <p className="font-bold text-sm mt-1">{c.name}</p>
                <p className="text-[10px] text-ink-500 leading-tight">{c.tagline}</p>
                {hero.class_key === c.key && <span className="chip-brand mt-1.5">CURRENT</span>}
              </button>))}
          </div>
        </div>
      )}

      {/* PASSWORD */}
      <div className="card p-5">
        <SectionHead title="Security" />
        <div className="space-y-3">
          <div><label className="label">Current password</label>
            <input className="input" type="password" value={pw.current}
              onChange={e => setPw({ ...pw, current: e.target.value })} /></div>
          <div><label className="label">New password</label>
            <input className="input" type="password" value={pw.next}
              onChange={e => setPw({ ...pw, next: e.target.value })} /></div>
          <button onClick={() => pwM.mutate()} disabled={pwM.isPending || pw.next.length < 6}
            className="btn-ghost w-full"><Lock size={14} /> Change password</button>
        </div>
      </div>

      <button onClick={() => { logout(); nav('/'); }}
        className="btn w-full bg-red-50 text-red-600 font-bold"><LogOut size={15} /> Log out</button>

      <Sheet open={qrOpen} onClose={() => setQrOpen(false)} title="My profile QR">
        <div className="text-center">
          <div className="inline-block p-4 bg-white rounded-2xl border-2 border-ink-100">
            <QRCodeSVG value={`${location.origin}/u/${user?.username}`} size={190} fgColor="#0f172a" />
          </div>
          <p className="font-black text-lg mt-3">@{user?.username}</p>
          <p className="text-sm text-ink-500">Referral code: <b>{user?.referral_code}</b></p>
          <button onClick={() => { navigator.clipboard.writeText(`${location.origin}/join?ref=${user?.referral_code}`); toast.success('Invite link copied!'); }}
            className="btn-primary btn-sm mt-3"><Copy size={13} /> Copy invite link</button>
        </div>
      </Sheet>
    </div>
  );
}